<template>
  <v-dialog
    v-model="open"
    max-width="560"
  >
    <v-card>
      <v-card-title>Change size</v-card-title>
      <v-card-text>
        <div class="body-2 mb-3">
          Now: <span class="font-weight-bold">{{ job.coreProductName }}</span>
        </div>
        <v-autocomplete
          v-model="coreProductId"
          :items="items"
          item-text="text"
          item-value="id"
          label="New size"
          outlined
          dense
          hide-details
          :loading="loadingChoices"
          @change="preview"
        />

        <v-progress-linear
          v-if="previewing"
          indeterminate
          class="mt-4"
        />
        <template v-else-if="plan">
          <v-alert
            v-if="plan.error"
            type="error"
            text
            dense
            class="mt-4 mb-0"
          >
            {{ plan.error }}
          </v-alert>
          <v-simple-table
            v-else
            dense
            class="mt-4"
          >
            <tbody>
              <tr>
                <td>Prints as</td>
                <td>{{ plan.printProductName }}</td>
              </tr>
              <tr>
                <td>Quantity</td>
                <td>{{ plan.quantity }} · {{ plan.colorSpec }}</td>
              </tr>
              <tr>
                <td>Turnaround</td>
                <td>
                  {{ plan.turnaround }}
                  <span
                    v-if="plan.previousTurnaround && plan.previousTurnaround !== plan.turnaround"
                    class="grey--text"
                  >
                    (was {{ plan.previousTurnaround }})
                  </span>
                </td>
              </tr>
              <tr>
                <td>Options</td>
                <td class="py-1">
                  <div>Kept where the new product has them, otherwise its defaults.</div>
                  <div
                    v-if="plan.unsetOptionGroups.length"
                    class="warning--text"
                  >
                    Pick in the drawer after saving: {{ plan.unsetOptionGroups.join(', ') }}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Price</td>
                <td class="py-1">
                  <div>
                    Customer paid {{ plan.paidPrice | currency }} · new size
                    {{ plan.newPrice | currency }}
                    <span class="font-weight-bold">({{ signed(plan.newPrice - plan.paidPrice) }})</span>
                  </div>
                  <div class="grey--text">
                    The customer's price is not changed. Print and turnaround only.
                  </div>
                </td>
              </tr>
              <tr>
                <td>Our cost</td>
                <td>
                  {{ plan.originalCost | currency }} → {{ plan.newCost | currency }}
                  <span class="font-weight-bold">({{ signed(plan.newCost - plan.originalCost) }})</span>
                </td>
              </tr>
              <tr>
                <td>Artwork</td>
                <td class="py-1">
                  Approved print files are removed. Open Edit Artwork afterward to resize the design.
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="open = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          elevation="0"
          :disabled="!plan || !!plan.error || previewing"
          :loading="saving"
          @click="save"
        >
          Change size
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PrintJobChangeSize',

  props: {
    value: { type: Boolean, default: false },
    job: { type: Object, required: true }
  },

  data: () => ({
    choices: [],
    loadingChoices: false,
    coreProductId: null,
    plan: null,
    previewing: false,
    saving: false
  }),

  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    // Grouped by category, the job's own category first.
    items() {
      const categories = [...new Set(this.choices.map((x) => x.categoryName))].sort((a, b) =>
        a === this.job.categoryName ? -1 : b === this.job.categoryName ? 1 : a.localeCompare(b)
      );
      return categories.flatMap((category) => [
        { header: category },
        ...this.choices
          .filter((x) => x.categoryName === category && x.id !== this.job.coreProductId)
          .map((x) => ({ id: x.id, text: x.name }))
      ]);
    }
  },

  watch: {
    value(val) {
      if (!val) return;
      this.coreProductId = null;
      this.plan = null;
      if (!this.choices.length) this.loadChoices();
    }
  },

  methods: {
    loadChoices() {
      this.loadingChoices = true;
      this.$http
        .get(`orders/getCoreProductChoices?masterAccountId=${this.job.masterAccountId}`)
        .then((response) => {
          this.choices = response.data;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loadingChoices = false;
        });
    },

    preview() {
      this.plan = null;
      if (!this.coreProductId) return;
      this.previewing = true;
      this.$http
        .get(
          `orders/previewCoreProductChange?jobId=${this.job.jobId}&newCoreProductId=${this.coreProductId}`
        )
        .then((response) => {
          this.plan = response.data;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.previewing = false;
        });
    },

    save() {
      this.saving = true;
      this.$store
        .dispatch('orders/changeCoreProduct', {
          orderId: this.job.orderId,
          jobId: this.job.jobId,
          newCoreProductId: this.coreProductId
        })
        .then(() => {
          this.$emit('changed');
          this.open = false;
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },

    signed(amount) {
      const formatted = this.$options.filters.currency(Math.abs(amount));
      return (amount < 0 ? '-' : '+') + formatted;
    }
  }
};
</script>

<style scoped>
tr td:first-child {
  font-weight: 600;
  width: 100px;
}
</style>
